import React, { useEffect, useState } from 'react';
import download from 'downloadjs';
import {
  Box,
  extendTheme,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Spinner,
  Stack,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { TbFileSearch } from 'react-icons/all';
import { MultiSelect } from 'react-multi-select-component';
import {
  Button,
  Datepicker,
  Heading,
  Select,
  StatusBadge,
  Table,
} from '~/components';
import { RoleEnum, SelectItem } from '~/types';
import {
  getBussinessInformation,
  getComissionadoAsSelect,
  getGrupoeconomicoAsSelect,
  getXlsxComissionado,
} from '~/services/BusinessInformation';
import { BusinessInformation as IbusinessInformation } from '~/types/businesseInformation';
import { formatCurrency } from '~/utils/formatCurrency';
import { formatInTimeZone } from '~/utils/formatToTimeZone';
import { STORAGE } from '~/constants';
import { MultiSelectStrings } from '~/utils/multiSelectStrings';
import { useViewCommission } from '~/hooks';

export const BusinessInformation: React.VFC = () => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
  } = useForm();
  const { errors } = formState;

  const statusComissao = [
    { value: '', label: 'Todas' },
    { value: '1', label: 'Pagas' },
    { value: '0', label: 'Em aberto' },
    { value: '2', label: 'Vencidas' },
  ];

  const [dataInicial, setDataInicial] = useState<Date>(new Date());
  const [dataFinal, setDataFinal] = useState<Date>(new Date());
  const [groups, setGroups] = useState<SelectItem[]>([]);
  const [comissionadosSelect, setComissionados] = useState<SelectItem[]>([]);
  const [formattedGroups, setFormattedGroups] = useState<any[]>([]);
  const [formattedCommissioned, setFormattedCommissioned] = useState<any[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<SelectItem[]>([]);
  const [selectedCommissioned, setSelectedCommissioned] = useState<
    SelectItem[]
  >([]);
  const [isFetching, setIsFetching] = useState(false);
  const [data, setData] = useState<IbusinessInformation[]>([]);
  const toast = useToast();
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const user = JSON.parse(localStorage.getItem(`${STORAGE}:user`) || '{}');

  const [commissionStatus, setCommissionStatus] = useState('');

  useEffect(() => {
    getGrupoeconomicoAsSelect().then(response => {
      setGroups(response);
    });
    getComissionadoAsSelect().then(response => {
      setComissionados(response);
    });
  }, []);

  useEffect(() => {
    const formatted = groups.map(it => {
      return { label: it.label, value: Number(it.value) };
    });
    setFormattedGroups([...formatted]);
    const comissionado = comissionadosSelect.map(it => {
      return { label: it.label, value: it.value };
    });
    setFormattedCommissioned([...comissionado]);
  }, [groups, comissionadosSelect]);

  const handleDataColeta = (date: Date) => {
    clearErrors('data_inicial');
    setDataInicial(date);
  };
  const handleDataFinal = (date: Date) => {
    clearErrors('data_final');
    setDataFinal(date);
  };

  const handleSelectedGroupsChange = async (itens?: SelectItem[]) => {
    setSelectedGroups(itens || []);
    getComissionadoAsSelect(itens?.map(it => it.value).join(',')).then(
      response => {
        setComissionados(response);
        setSelectedCommissioned([]);
      },
    );
  };

  const handleSelectedComissionadoChange = async (items?: SelectItem[]) => {
    setSelectedCommissioned(items || []);
  };

  const handleFaturamentoChange = (e: any) => {
    setCommissionStatus(e.target.value);
  };

  const handleDownload = async () => {
    const grupos = selectedGroups.map(it => it.value).join(',');
    const comissionados = selectedCommissioned.map(it => it.value).join(',');
    const dataIncio = dataInicial.toISOString().substring(0, 10);
    const dataFim = dataFinal.toISOString().substring(0, 10);
    setIsLoadingDownload(true);
    getXlsxComissionado(
      dataIncio,
      dataFim,
      grupos,
      comissionados,
      commissionStatus,
    )
      .then(r => {
        if (r.data.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
        setIsLoadingDownload(false);
      })
      .catch(err => {
        toast({
          title: `Erro`,
          description: err.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setIsLoadingDownload(false);
      });
  };

  const breakpoints = {
    base: '0px',
    sm: '320px',
    md: '768px',
    lg: '960px',
    xl: '1285px',
    '2xl': '1536px',
  };

  const theme = extendTheme({ breakpoints });

  const isParceiroComercial = user?.id_funcao === RoleEnum.PARCEIRO_COMERCIAL;
  const viewCommission = useViewCommission();
  const isGerente = user?.id_funcao === RoleEnum.GERENTE_CONTAS;

  const cantSeeComissions =
    isParceiroComercial || (isGerente && !viewCommission);

  async function consultar() {
    const grupos = selectedGroups.map(it => it.value).join(',');
    const comissionados = selectedCommissioned.map(it => it.value).join(',');
    const dataIncio = dataInicial.toISOString().substring(0, 10);
    const dataFim = dataFinal.toISOString().substring(0, 10);
    setIsFetching(true);
    getBussinessInformation(
      dataIncio,
      dataFim,
      grupos,
      comissionados,
      commissionStatus,
    )
      .then(response => {
        setData(response);
      })
      .catch(error => {
        if (error.response.status === 400) {
          toast({
            title: `Erro ao consultar informações comerciais`,
            description: error.response.data.detail,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          toast({
            title: `Erro ao consultar informações comerciais`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setIsFetching(false);
      });
  }

  return (
    <Box px="8" py="4">
      <Stack gridGap={4}>
        <HStack justifyContent="space-between">
          <Heading>Informações Comerciais</Heading>
          <Button
            onClick={handleDownload}
            color="white"
            background="gray.900"
            _hover={{
              bgColor: 'black',
            }}
            style={{ marginTop: '20px' }}
          >
            Exportar
          </Button>
        </HStack>
        <Flex
          alignItems="end"
          gridColumnGap={2}
          as="form"
          onSubmit={handleSubmit(consultar)}
          flexWrap="wrap"
          gridRowGap={4}
        >
          <HStack
            width={{
              base: '100%',
              sm: '100%',
              md: '60%',
              xl: '30%',
              '2xl': '20%',
            }}
            alignItems="end"
          >
            <FormControl isInvalid={errors.data_inicial}>
              <FormLabel fontSize="14px">Período de faturamento</FormLabel>
              <Datepicker
                onChange={e => handleDataColeta(e)}
                selectedDate={dataInicial}
                error={errors.data_inicial}
              />
            </FormControl>
            <Text style={{ marginBottom: '7px' }}>a</Text>
            <FormControl
              isInvalid={errors.data_final}
              isRequired
              marginTop="auto"
            >
              <Datepicker
                onChange={e => handleDataFinal(e)}
                selectedDate={dataFinal}
                error={errors.data_final}
              />
            </FormControl>
          </HStack>
          <Stack
            width={{
              base: '100%',
              sm: '100%',
              md: '30%',
              xl: '10%',
              '2xl': '10%',
            }}
          >
            <Select
              name="status_comissao"
              placeholder="Selecione"
              label="Status das faturas"
              error={errors.status_comissao}
              fontSize="14px"
              whiteSpace="nowrap"
              onChange={e => handleFaturamentoChange(e)}
            >
              {statusComissao?.map(item => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Select>
          </Stack>
          <HStack
            width={
              !cantSeeComissions
                ? {
                    base: '100%',
                    sm: '100%',
                    md: '45%',
                    xl: '25%',
                    '2xl': '30%',
                  }
                : {
                    base: '100%',
                    sm: '100%',
                    md: '80%',
                    xl: '50%',
                    '2xl': '60%',
                  }
            }
            alignItems="end"
          >
            <FormControl isInvalid={errors.grupos} marginTop="auto">
              <FormLabel fontSize="14px">Grupos de Clientes</FormLabel>
              <MultiSelect
                value={selectedGroups}
                options={groups}
                onChange={(changes: SelectItem[]) =>
                  handleSelectedGroupsChange(changes)
                }
                labelledBy="Selecione"
                overrideStrings={MultiSelectStrings}
              />
            </FormControl>
          </HStack>
          <HStack
            width={
              !cantSeeComissions
                ? {
                    base: '100%',
                    sm: '100%',
                    md: '50%',
                    xl: '30%',
                    '2xl': '35%',
                  }
                : {
                    base: '100%',
                    sm: '100%',
                    md: '10%',
                    xl: '5%',
                    '2xl': '5%',
                  }
            }
            alignItems="end"
            justifyContent="space-between"
          >
            {!cantSeeComissions && (
              <FormControl
                isInvalid={errors.comissionado}
                marginTop="auto"
                maxW="80%"
              >
                <FormLabel fontSize="14px">Comissionado</FormLabel>
                <MultiSelect
                  value={selectedCommissioned}
                  options={comissionadosSelect}
                  onChange={(changes: SelectItem[]) =>
                    handleSelectedComissionadoChange(changes)
                  }
                  labelledBy="Selecione"
                  overrideStrings={MultiSelectStrings}
                />
              </FormControl>
            )}
            <Button
              minW="100px"
              type="submit"
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px' }}
              w="fit-content"
              isDisabled={isFetching}
              px={5}
            >
              Consultar
            </Button>
          </HStack>
        </Flex>
        <HStack gridGap={0} justifyContent="center">
          {!isFetching && data.length > 0 ? (
            <Table type="secondary" size="sm" marginStart="0px">
              <Thead>
                <Tr>
                  <Th>COMISSIONADO</Th>
                  <Th borderRight="1px solid #e5e5e6">N° CONTRATO SAP</Th>
                  <Th width="500px">GRUPO ECONÔMICO</Th>
                  <Th borderRight="1px solid #e5e5e6" textAlign="right">
                    VALOR TOTAL CONTRATO
                  </Th>
                  <Th width="95px">FATURA</Th>
                  <Th textAlign="right">VALOR COMISSÃO</Th>
                  <Th textAlign="center">STATUS PGTO FATURA</Th>
                  <Th>DATA DO FATURAMENTO</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.map(info => (
                  <Tr>
                    <Td fontWeight="bold">{info.nome}</Td>
                    <Td borderRight="1px solid #e5e5e6">
                      <Text fontWeight="bold">{info.nu_contrato}</Text>
                      <Text>{info.nu_pedido}</Text>
                    </Td>
                    <Td>
                      <Text fontWeight="bold" noOfLines={1}>
                        {info.nm_grupoeconomico}
                      </Text>
                      <Text noOfLines={1}>{info.nm_razaosocial}</Text>
                    </Td>
                    <Td borderRight="1px solid #e5e5e6" textAlign="right">
                      {formatCurrency(Number(info.valor))}
                    </Td>
                    <Td>{info.nu_fatura}</Td>
                    <Td textAlign="right">
                      {formatCurrency(Number(info.valor_comissao))}
                    </Td>
                    <Td textAlign="center">
                      <StatusBadge
                        variant={
                          info.st_pagamento === '1'
                            ? 'green'
                            : info.st_pagamento === '0'
                            ? 'purple'
                            : 'red'
                        }
                      >
                        {info.st_pagamento === '1'
                          ? 'Paga'
                          : info.st_pagamento === '0'
                          ? 'Em aberto'
                          : 'Vencida'}
                      </StatusBadge>
                    </Td>
                    <Td>
                      {formatInTimeZone(info.dt_faturamento, 'dd/MM/yyyy') ||
                        '-'}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : !isFetching && !data.length ? (
            <Stack alignItems="center" gridGap={2} m={5}>
              <Icon as={TbFileSearch} fontSize="50px" />
              <Text>
                Informações comerciais disponibilizadas a partir de 2022.
                Selecione o período para gerar o relatório.
              </Text>
            </Stack>
          ) : (
            <Stack alignItems="center" gridGap={2} m={5}>
              <Spinner size="xl" speed="1s" />
              <Text>Gerando relatório</Text>
            </Stack>
          )}
        </HStack>
      </Stack>
    </Box>
  );
};
